// owner-map: REG-29
// First-contact tips: the new-user introduction behind `chatboard-onboarding-tips`.
//
// Each tip points at a control or surface the first time it is on screen, and retires for
// good once the user uses that feature or dismisses the tip. Tips never block, wait or
// script anything; at most one shows at a time (lib/onboarding/tipEngine.js picks it).
//
// Adding a tip for a new feature is one entry here. Rules that keep the list valid as the
// UI changes:
// - `anchor` names registered `data-ui` ids / surfaces only (tips.test.js checks the
//   registries), never a CSS class or position. A list of anchors is tried in order.
// - `text` names the control, never where it sits on screen ("the icons on the left").
// - Order is priority: when several tips could show, the earliest wins. Tips about something
//   the user just opened come before tips about what is always there.
//
// Fields:
//   id           — stable; it is the persisted retire key, so renaming one re-shows it.
//   anchor       — [{ui, key?}|{surface}, optional `where` and `within: <surface>`] — first
//                  visible match wins. `key` picks one of a repeated control (its `data-ui-key`);
//                  `where` requires data attributes on it (e.g. the list a card is in).
//   text         — one or two sentences; **bold** marks the action.
//   retireOn     — {intents?, surfaces?, controls?, clicks?}: retired when one of these intents
//                  begins, one of these surfaces or `data-ui` controls is on screen, or one of
//                  the `clicks` controls is clicked (for a control whose action has no intent).
//   once         — retired as soon as it has been shown (purely informational tips).
//   whilePresent — [surface, ...]: shown only while all of these are on the page.
//   whileAbsent  — [surface, ...]: shown only while none of these is on the page.
//   afterIntents — [name | {name, params}, ...]: shown only once the user has done one of these
//                  (params must match, e.g. an add to the watchlist, not a removal).
//   after        — [tip id, ...]: shown only once each of these tips has had its turn (retired,
//                  or shown or stepped aside this visit). Use it where order matters more than
//                  which anchor happens to render first.
//   fromVisit    — shown only from this visit on (default 1, the first).
//   answersAction — the tip answers something the user just did (opened a panel, asked a
//                  question), so it shows the first time whatever the visit's quota; tips without
//                  it interrupt, and at most MAX_TIPS_PER_VISIT of those show per visit.
//   beside       — the card sits beside the anchor (its right, else its left), pointing at it,
//                  rather than below or above it: for a dialog in the middle of the screen, or a
//                  row in a side panel whose neighbouring rows the card must not cover.
//   above        — the card sits above the anchor when it fits, else below: for a control with
//                  related controls below it that the card must not cover.
//   namesPositions — the text names a position on purpose (a layout it describes, not where a
//                  control sits), so the position-word check skips it.

import { tipHelpText } from "../lib/onboarding/tipEngine.js";

/**
 * Surfaces that mean the forecast page is still loading or running. Tips about the forecast page
 * wait while one is on screen (`whileAbsent`), and a tip named in another's `after` is never
 * skipped for having no control yet.
 */
export const PAGE_LOADING_SURFACES = ["run-status", "preprocess"];

export const TIPS = [
  {
    id: "featured",
    anchor: [{ surface: "ask-hero.featured" }, { ui: "ask-hero.example" }],
    text: "**Pick one of these** to see how Sayer works, or ask a question of your own.",
    retireOn: { intents: ["sayer_forecast.open", "forecast.submit"] },
  },
  {
    id: "question-review",
    answersAction: true,
    // The dialog, not the `preprocess` surface: that is the full-screen backdrop, and a card
    // placed against it lands on top of the dialog.
    anchor: [{ ui: "preprocess.dialog" }],
    beside: true,
    text: "Sayer removes ambiguity from your question. Look over the wording, the deadline and how it resolves, then confirm.",
    once: true,
  },
  {
    id: "run-status",
    answersAction: true,
    // The card, not the `run-status` surface: that is the backdrop over the whole canvas, and a
    // card placed against it lands at the top of the screen, far from the progress card.
    anchor: [{ ui: "run-status.card" }],
    beside: true,
    text: "This usually takes a minute or two. You can look around while it runs.",
    once: true,
  },
  {
    id: "update-settings",
    answersAction: true,
    anchor: [{ ui: "forecast-settings.cadence" }],
    // Out over the canvas: below the row it would cover the alert settings it mentions.
    beside: true,
    text: "You can adjust **reforecast frequency**, and whether it **emails you** when the probability shifts.",
    retireOn: { intents: ["forecast_settings.change"] },
  },
  {
    id: "watchlist",
    answersAction: true,
    // A Recent card in the board, where a drag can move it into the Watchlist (search results
    // reorder only within their own list). With none on screen the tip waits.
    anchor: [{ ui: "forecast-manager.row", where: { "data-board-list": "recent" } }],
    text: "**Click and drag** a forecast into your **Watchlist** to keep it first in your list.",
    retireOn: { intents: ["forecast.pin"] },
  },
  {
    id: "watch-settings",
    answersAction: true,
    // A watched card: only its menu offers Tracking settings (an unwatched shared one offers
    // Access settings). With none on screen the tip waits.
    anchor: [
      { ui: "forecast-manager.row", where: { "data-board-list": "watchlist" } },
      { ui: "forecast-manager.row", where: { "data-sortable-section": "watchlist" } },
    ],
    text: "On a watched forecast, open **⋯** and choose **Tracking settings** to set how often Sayer reforecasts, sharing, and alerts.",
    afterIntents: [{ name: "forecast.pin", params: { pinned: true } }],
    // The cadence control, not the panel: the panel also opens as Access settings, without it.
    retireOn: { intents: ["forecast_settings.change"], controls: ["forecast-settings.cadence"] },
  },
  {
    id: "signals",
    answersAction: true,
    // Replaces the panel's old first-open note. Points at Sayer's picks for the question, else
    // the user's own list; picking either plots it (no intent, so the click retires the tip).
    anchor: [{ ui: "signal-library.suggestion-plot" }, { ui: "signal-library.signal-toggle" }],
    // Over the list's heading: below the first signal it would cover the others to pick from.
    above: true,
    text: "**Pick a signal** to show it alongside your forecast, with its history and projected path.",
    retireOn: {
      intents: ["signal.add"],
      clicks: ["signal-library.suggestion-plot", "signal-library.signal-toggle"],
    },
  },
  {
    id: "chat",
    answersAction: true,
    // The chat box shows only once the user has opened Chat: the tip waits for that.
    anchor: [{ ui: "chat.input" }],
    text: "**Ask Sayer** about this forecast, what could change it, try a scenario or make a new forecast from here.",
    retireOn: { intents: ["chat.ask"] },
    whilePresent: ["forecast-header"],
    whileAbsent: PAGE_LOADING_SURFACES,
  },
  {
    id: "timeline",
    anchor: [{ surface: "timeline.canvas" }],
    text: "This is the forecast's timeline. What's already happened sits before the **Today** line with triggers that could still happen after it. Sayer's answer is on the right.",
    namesPositions: true,
    once: true,
    whilePresent: ["forecast-header"],
    whileAbsent: PAGE_LOADING_SURFACES,
  },
  {
    id: "report",
    anchor: [{ ui: "header.open-report" }],
    text: "**Open the report** to see why Sayer gives this answer and what it's based on.",
    retireOn: { intents: ["report.open"] },
    whileAbsent: PAGE_LOADING_SURFACES,
  },
  {
    id: "triggers",
    anchor: [{ ui: "timeline.event" }],
    text: "Each circle is something that could happen in the future. **Hover over** to see how it changes the forecast. **Click** to explore it.",
    retireOn: { intents: ["forecast.branch"] },
    whileAbsent: PAGE_LOADING_SURFACES,
  },
  {
    id: "own-trigger",
    anchor: [{ ui: "timeline.resolution-marker", within: "timeline.branch" }],
    text: "This is the forecast if that trigger happens. **Double-click the timeline** to add a trigger of your own.",
    retireOn: { surfaces: ["custom-trigger"] },
    whileAbsent: PAGE_LOADING_SURFACES,
  },
  {
    id: "forecasts",
    anchor: [{ ui: "nav-rail.section", key: "forecasts" }],
    text: "**Forecasts** keeps every forecast you've made, so you can come back to any of them.",
    retireOn: { surfaces: ["forecast-manager"] },
    whilePresent: ["forecast-header"],
    after: ["timeline", "report", "triggers"],
    whileAbsent: PAGE_LOADING_SURFACES,
  },
  {
    id: "updates",
    anchor: [{ ui: "header.updates" }],
    text: "**Updates** sets how often Sayer re-checks this forecast, and can email you when the probability shifts.",
    // Opening the Tracking settings it points at hands over to the update-settings tip (the
    // cadence control, not the panel, which also opens as Access settings).
    retireOn: { intents: ["forecast_settings.change"], controls: ["forecast-settings.cadence"] },
    fromVisit: 2,
  },
];

/** The tips as plain help text, for the chat's "how do I…" answers. */
export const TIPS_HELP_TEXT = tipHelpText(TIPS);
